import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export function ReplyCreditPacks({ onPurchased }) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(() =>
    api.get("/payments/reply-credits/config").then(({ data }) => setCfg(data)).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);

  const buy = async (bundle) => {
    setBusy(bundle.quantity);
    try {
      const { data: order } = await api.post("/payments/reply-credits/order", { quantity: bundle.quantity });
      const finalize = async (payload) => {
        const { data } = await api.post("/payments/reply-credits/verify", payload);
        toast.success(`Added ${data.credits_added} reply credits to your account.`);
        await refresh();
        onPurchased?.(data);
      };
      if (order.mock_mode || !order.key_id) {
        await finalize({ order_id: order.order_id });
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay checkout."); return; }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "JobBasket.Network",
        description: `${order.credits} Reply Credits`,
        order_id: order.order_id,
        prefill: { email: user?.email, name: user?.name },
        theme: { color: "#f59e0b" },
        handler: async (res) => {
          try {
            await finalize({
              order_id: res.razorpay_order_id,
              payment_id: res.razorpay_payment_id,
              signature: res.razorpay_signature,
            });
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (!cfg) {
    return <div className="py-4 flex justify-center" data-testid="reply-packs-loading"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  }
  const q = cfg.quota || {};
  const unlimited = q.remaining == null;
  return (
    <div data-testid="reply-credit-packs">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-600">
          Reply balance: <span className="font-semibold text-slate-900" data-testid="reply-packs-balance">{unlimited ? "Unlimited" : q.remaining}</span>
          {!unlimited && (
            <span className="text-slate-400 text-xs ml-1">
              ({q.base_remaining ?? 0} free this month{q.extra_credits ? ` · ${q.extra_credits} purchased` : ""})
            </span>
          )}
        </p>
        <p className="text-xs text-slate-500">₹{cfg.price_per_credit} per reply · never expire</p>
      </div>
      {unlimited ? (
        <p className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="reply-packs-unlimited">
          Your plan already includes unlimited replies — no top-up needed.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3 mt-3">
          {cfg.bundles.map((b) => (
            <button
              key={b.quantity}
              data-testid={`reply-pack-buy-${b.quantity}`}
              onClick={() => buy(b)}
              disabled={busy !== null}
              className="rounded-xl border border-amber-200 bg-amber-50/60 hover:bg-amber-100/70 transition-colors px-3 py-3 text-center disabled:opacity-60"
            >
              {busy === b.quantity ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto text-amber-600" />
              ) : (
                <>
                  <p className="text-lg font-bold text-slate-900">{b.quantity}</p>
                  <p className="text-[11px] text-slate-500">replies</p>
                  <p className="text-sm font-semibold text-amber-700 mt-1">₹{b.amount}</p>
                </>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
